import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import he from './he.json';

export const LOCALES = [
  { code: 'he', label: 'עברית', dir: 'rtl' },
  { code: 'th', label: 'ไทย', dir: 'ltr' },
  { code: 'en', label: 'English', dir: 'ltr' },
] as const;
export type Locale = (typeof LOCALES)[number]['code'];

const STORAGE_KEY = 'farmos.locale';

function initialLocale(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'he' || saved === 'th' || saved === 'en') return saved;
  } catch {
    /* storage unavailable */
  }
  return 'he';
}

/**
 * Hebrew ships with the app; English and Thai load when someone uses them, so most users
 * don't download them. Thai falls back to English, so it brings English along.
 */
const LOADERS = {
  en: () => import('./en.json'),
  th: () => import('./th.json'),
} as const;

async function loadLocale(locale: Locale) {
  const needed =
    locale === 'th' ? (['th', 'en'] as const) : locale === 'en' ? (['en'] as const) : [];
  await Promise.all(
    needed
      .filter((l) => !i18n.hasResourceBundle(l, 'translation'))
      .map(async (l) =>
        i18n.addResourceBundle(l, 'translation', (await LOADERS[l]()).default, true, true),
      ),
  );
}

export function applyLocale(locale: Locale) {
  const dir = LOCALES.find((l) => l.code === locale)?.dir ?? 'rtl';
  document.documentElement.lang = locale;
  document.documentElement.dir = dir;
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* storage unavailable */
  }
  void loadLocale(locale).then(() => i18n.changeLanguage(locale));
}

void i18n.use(initReactI18next).init({
  resources: { he: { translation: he } },
  lng: initialLocale(),
  // Thai workers read English more easily than Hebrew
  fallbackLng: { th: ['en', 'he'], en: ['he'], default: ['he'] },
  interpolation: { escapeValue: false },
  returnNull: false,
});

// An English or Thai user sees Hebrew for the moment it takes to fetch their strings.
void loadLocale(i18n.language as Locale).then(() => i18n.changeLanguage(i18n.language));

if (typeof document !== 'undefined') {
  const l = i18n.language as Locale;
  document.documentElement.lang = l;
  document.documentElement.dir = LOCALES.find((x) => x.code === l)?.dir ?? 'rtl';
}

export default i18n;
